import { OneWayDragger } from "./OneWayDragger";
import { trackShape } from "@/analytics/drawing-analytics";
import { TwoPointDragger } from "./TwoPointDragger";
import { DualModeDragger } from "./DualModeDragger";
import {
  DraggerType,
  type Point2D,
  type Point3D,
  type DraggerConfig,
} from "./types";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { AddEntitiesCommand } from "../commands/AddEntitiesCommand";
import { PREVIEW_SHAPE_STYLE } from "./previewStyles";
import { MeasurementLabels } from "../overlay/MeasurementLabels";
import { getBoundsFromPoints, type PointBounds } from "./pointGeometry";

/**
 * Build an OdDbPolyline as a closed rectangle from corner to corner.
 * Pure/shared: used by both the classic single-stroke engine and the
 * two-point sequential engine below.
 */
function buildRectangleEntity(
  viewer: DRDrawingViewer,
  min: Point3D,
  max: Point3D,
): { id: DrawingModule.OdDbObjectId } | null {
  const module = viewer.module;
  const db = viewer.database;

  if (!db) {
    return null;
  }

  const polyline = module.OdDbPolyline.createObject();
  polyline.setDatabaseDefaults(db, true);

  const corners = [
    { x: min.x, y: min.y }, // Bottom-left
    { x: max.x, y: min.y }, // Bottom-right
    { x: max.x, y: max.y }, // Top-right
    { x: min.x, y: max.y }, // Top-left
  ];

  for (let i = 0; i < corners.length; i++) {
    const point2d = new module.OdGePoint2d(corners[i].x, corners[i].y);
    polyline.addVertexAt(i, point2d, 0, 0, 0, 0);
    deleteObj(point2d);
  }

  polyline.setClosed(true);

  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  const entityId = currentSpace.appendOdDbEntity(polyline);

  deleteObj(currentSpace);
  deleteObj(polyline);

  return { id: entityId };
}

/**
 * Classic single-stroke rectangle: press = corner A, release = corner B.
 * Body unchanged from the original RectangleDragger — only the raw entity
 * geometry (buildRectangleEntity) was pulled out so the two-point engine
 * below can reuse it.
 */
export class RectangleOneWayEngine extends OneWayDragger {
  readonly type = DraggerType.Rectangle;

  private rectPreviewId: string | null = null;
  private measureLabels: MeasurementLabels | null = null;

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.rectPreviewId = overlay.createRectangle(
      screen.x,
      screen.y,
      0,
      0,
      PREVIEW_SHAPE_STYLE,
    );
    if (this.rectPreviewId) {
      this.addPreviewId(this.rectPreviewId);
    }
  }

  protected updatePreview(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !this.rectPreviewId) return;

    const bounds = this.getBounds();
    if (!bounds) return;

    overlay.update(this.rectPreviewId, {
      x: bounds.screenMin.x,
      y: bounds.screenMin.y,
      width: bounds.width,
      height: bounds.height,
    });

    this.updateMeasurements(bounds);
  }

  private updateMeasurements(bounds: PointBounds): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }
    this.measureLabels.setLine(
      "width",
      bounds.screenMin.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMin.y,
    );
    this.measureLabels.setLabel(
      "width",
      bounds.screenMin.x + bounds.width / 2,
      bounds.screenMin.y - 18,
      MeasurementLabels.formatDistance(bounds.worldWidth),
    );
    this.measureLabels.setLine(
      "height",
      bounds.screenMax.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMax.y,
    );
    this.measureLabels.setLabel(
      "height",
      bounds.screenMax.x + 18,
      bounds.screenMin.y + bounds.height / 2,
      MeasurementLabels.formatDistance(bounds.worldHeight),
    );
  }

  // ============== Entity Creation ==============

  protected createEntity(): void {
    const bounds = this.getBounds();
    if (!bounds) return;

    const db = this.viewer.database;
    if (!db) return;

    const noteConfig = this.viewer.activeNoteConfig;
    const entityInfo = buildRectangleEntity(
      this.viewer,
      bounds.worldMin,
      bounds.worldMax,
    );

    if (entityInfo) {
      this.applyNoteConfigToEntity(entityInfo.id);

      const entityIds: DrawingModule.OdDbObjectId[] = [entityInfo.id];

      const hatchId = this.createHatchForEntity(entityInfo.id);
      if (hatchId) {
        entityIds.push(hatchId);
      }

      const centerX = (bounds.worldMin.x + bounds.worldMax.x) / 2;
      const centerY = (bounds.worldMin.y + bounds.worldMax.y) / 2;
      const labelId = this.createLabelIfEnabled(centerX, centerY);
      if (labelId) entityIds.push(labelId);

      const command = new AddEntitiesCommand(this.viewer, {
        entityHandles: entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: noteConfig.props.layername ?? "0",
      });
      this.viewer.executeCommand(command);
      trackShape("rectangle");

      const extras = labelId ? [labelId] : [];
      this.selectCreatedEntity(entityInfo.id, extras);
      for (const id of entityIds) {
        deleteObj(id);
      }
    }
  }

  protected clear(): void {
    super.clear();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.rectPreviewId = null;
  }
}

/**
 * Two-point rectangle: press-drag-release places corner A, press-drag-release
 * again places corner B and creates the entity. Same preview/entity behavior
 * as RectangleOneWayEngine, adapted to read from the two fixed points instead
 * of a single continuous start/current drag.
 */
export class RectangleTwoPointEngine extends TwoPointDragger {
  readonly type = DraggerType.Rectangle;

  private rectPreviewId: string | null = null;
  private measureLabels: MeasurementLabels | null = null;

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.rectPreviewId = overlay.createRectangle(
      screen.x,
      screen.y,
      0,
      0,
      PREVIEW_SHAPE_STYLE,
    );
    if (this.rectPreviewId) {
      this.addPreviewId(this.rectPreviewId);
    }
  }

  protected updatePreview(screen: Point2D, world: Point3D): void {
    const overlay = this.viewer.overlayManager;
    const anchorWorld = this.getSnapReferencePoint();
    if (!overlay || !this.rectPreviewId || !anchorWorld) return;

    const anchorScreen = this.viewer.worldToScreen(anchorWorld);
    const bounds = getBoundsFromPoints(
      anchorScreen,
      screen,
      anchorWorld,
      world,
    );

    overlay.update(this.rectPreviewId, {
      x: bounds.screenMin.x,
      y: bounds.screenMin.y,
      width: bounds.width,
      height: bounds.height,
    });

    this.updateMeasurements(bounds);
  }

  private updateMeasurements(bounds: PointBounds): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }
    this.measureLabels.setLine(
      "width",
      bounds.screenMin.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMin.y,
    );
    this.measureLabels.setLabel(
      "width",
      bounds.screenMin.x + bounds.width / 2,
      bounds.screenMin.y - 18,
      MeasurementLabels.formatDistance(bounds.worldWidth),
    );
    this.measureLabels.setLine(
      "height",
      bounds.screenMax.x,
      bounds.screenMin.y,
      bounds.screenMax.x,
      bounds.screenMax.y,
    );
    this.measureLabels.setLabel(
      "height",
      bounds.screenMax.x + 18,
      bounds.screenMin.y + bounds.height / 2,
      MeasurementLabels.formatDistance(bounds.worldHeight),
    );
  }

  // ============== Entity Creation ==============

  protected createEntity(a: Point3D, b: Point3D): void {
    const db = this.viewer.database;
    if (!db) return;

    const bounds = getBoundsFromPoints(
      this.viewer.worldToScreen(a),
      this.viewer.worldToScreen(b),
      a,
      b,
    );

    const noteConfig = this.viewer.activeNoteConfig;
    const entityInfo = buildRectangleEntity(
      this.viewer,
      bounds.worldMin,
      bounds.worldMax,
    );

    if (entityInfo) {
      this.applyNoteConfigToEntity(entityInfo.id);

      const entityIds: DrawingModule.OdDbObjectId[] = [entityInfo.id];

      const hatchId = this.createHatchForEntity(entityInfo.id);
      if (hatchId) {
        entityIds.push(hatchId);
      }

      const centerX = (bounds.worldMin.x + bounds.worldMax.x) / 2;
      const centerY = (bounds.worldMin.y + bounds.worldMax.y) / 2;
      const labelId = this.createLabelIfEnabled(centerX, centerY);
      if (labelId) entityIds.push(labelId);

      const command = new AddEntitiesCommand(this.viewer, {
        entityHandles: entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: noteConfig.props.layername ?? "0",
      });
      this.viewer.executeCommand(command);
      trackShape("rectangle");

      const extras = labelId ? [labelId] : [];
      this.selectCreatedEntity(entityInfo.id, extras);
      for (const id of entityIds) {
        deleteObj(id);
      }
    }
  }

  protected override clearLiveMeasurements(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
  }

  protected clear(): void {
    super.clear();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.rectPreviewId = null;
  }
}

/**
 * Registered dragger for DraggerType.Rectangle. Routes to either the classic
 * single-stroke engine or the two-point sequential engine, toggled via
 * setTwoPointMode() (see DraggerMenuState.sequentialPointMode).
 */
export class RectangleDragger extends DualModeDragger<
  RectangleOneWayEngine,
  RectangleTwoPointEngine
> {
  readonly type = DraggerType.Rectangle;

  constructor(viewer: DRDrawingViewer, config: DraggerConfig = {}) {
    super(
      viewer,
      config,
      new RectangleOneWayEngine(viewer, config),
      new RectangleTwoPointEngine(viewer, config),
    );
  }
}
